import React, { useState } from "react";
import styled from "styled-components";
import { formatDate, formatUserName } from "utils";
import Collapse from "@material-ui/core/Collapse";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import Attribute from "components/common/Attribute";
import HiddenOn from "components/common/HiddenOn";
import EventHeaderActionPanel from "components/event/EventHeaderActionPanel";
import { SlotTooltip as Tooltip } from "components/event/OrbatNode";
import GetAppIcon from "@material-ui/icons/GetApp";
import config from "config";

const EventHeader = ({
  eventDate,
  eventTitle,
  eventDesc,
  eventOwner,
  currentEvent,
  userIsOwner,
  redirect,
  handleClick,
  id,
  eventRepo,
  total_slots,
  filled_slots,
  eventReserves,
  isMobile,
  liveUpdates,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const repoLink = config.repos[eventRepo];
  return (
    <Header>
      <Wrapper>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <Typography variant="h2" component="h2">
            <u>{eventTitle}</u>
          </Typography>

          <DataWrapper isMobile={isMobile}>
            <EventHeaderActionPanel
              currentEvent={currentEvent}
              userIsOwner={userIsOwner}
              redirect={redirect}
              handleClick={handleClick}
              id={id}
              isMobile={isMobile}
              eventTitle={eventTitle}
            />
            <div id={"summary"}>
              <Typography variant="h5" component="h5">
                <u>
                  <b>SUMMARY</b>
                </u>
              </Typography>
              <div id={"updaterIndicator"}>
                <Tooltip
                  title={
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <p>
                        This page will automatically update. However, this is
                        disabled if the page has been open too long or the event
                        date has already passed. Refresh the page to re-enable
                        automatic updates for events that haven't been played
                        yet.
                      </p>
                    </div>
                  }
                  arrow
                >
                  <div>
                    <HiddenOn
                      hideOn={!liveUpdates}
                      fallback={
                        <>
                          <UpdateCircle colour={"#fc0303"} />
                          {"Live Updates Inactive"}
                        </>
                      }
                    >
                      <>
                        <UpdateCircle colour={"#03fc28"} />
                        {"Live Updates Active"}
                      </>
                    </HiddenOn>
                  </div>
                </Tooltip>
              </div>
              <div>
                <div>
                  <Attribute
                    text={"Host:"}
                    value={formatUserName(eventOwner)}
                  />
                  <Attribute
                    text={"Date:"}
                    value={formatDate({ date: eventDate })}
                  />
                  <Attribute
                    text={"Event Repo:"}
                    value={
                      repoLink ? (
                        <a
                          id={"downloadRepoLink"}
                          href={repoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {eventRepo}
                          <GetAppIcon />
                        </a>
                      ) : (
                        eventRepo
                      )
                    }
                  />
                </div>
                <div>
                  <Attribute text={"Total Slots:"} value={total_slots} />
                  <Attribute text={"Filled Slots:"} value={filled_slots} />
                  <Attribute
                    text={"Open Slots:"}
                    value={total_slots - filled_slots}
                  />
                </div>
              </div>
            </div>
            <div
              id={"reserves"}
              onClick={() => {
                handleClick({
                  message: "Copied reserve command to clipboard.",
                });
                navigator.clipboard.writeText(`!reserve ${id}`);
              }}
            >
              <Typography variant="h5" component="h5">
                <u>
                  <b>RESERVES LIST</b>
                </u>
              </Typography>
              <Attribute
                text={"Number of Reserves:"}
                value={eventReserves.length}
              />
              <div id={"players"}>
                <Typography variant="body1" component="h5">
                  <b>RESERVES</b>
                </Typography>
                <div id={"players-list"}>
                  {eventReserves.map(({ player_name }) => (
                    <div>{player_name}</div>
                  ))}
                </div>
              </div>
            </div>
          </DataWrapper>
        </div>
      </Wrapper>

      <HiddenOn
        hideOn={isMobile}
        fallback={
          <a rel="noopener noreferrer" target="_blank" href={eventDesc}>
            Click here for a link to the briefing.
          </a>
        }
      >
        {/* Limit event briefing to google docs only for now, this can be abused, but it's a risk we'll have to take */}
        {eventDesc.includes("docs.google.com/document") && (
          <EmbeddedDoc isMobile={isMobile}>
            <div>
              <Switch
                checked={isOpen}
                onChange={() => setIsOpen(!isOpen)}
                aria-label="Collapse"
              />
              <b>Show Briefing</b>
            </div>
            <Collapse in={isOpen}>
              <iframe title={"briefing"} src={eventDesc} />
            </Collapse>
          </EmbeddedDoc>
        )}
      </HiddenOn>
    </Header>
  );
};

const UpdateCircle = styled.div`
  width: 15px;
  height: 15px;
  background-color: ${(props) => props.colour};
  border-radius: 50%;
  margin: 5px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;

  h2 {
    padding-bottom: 20px;
  }
`;

const EmbeddedDoc = styled.div`
  border-style: solid;
  border-color: #3b618ccc;
  border-radius: 8px;
  background-color: #36393f;
  border-width: 2px;
  padding: 5px;
  margin: 10px;

  iframe {
    height: 80vh;
    width: ${(props) => (!!props.isMobile ? "90vw" : "60vw")};
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  border-style: solid;
  border-color: #3b618ccc;
  border-radius: 8px;
  background-color: #36393f;
  border-width: 2px;
  padding: 5px;
  margin: 10px;

  > div {
    justify-content: center;
  }
`;

const DataWrapper = styled.div`
  display: flex;
  flex-direction: ${(props) => (!!props.isMobile ? "column" : "row")};
  width: 100%;

  > div {
    border-style: solid;
    border-color: #3b618ccc;
    border-radius: 8px;
    background-color: #36393f;
    border-width: 2px;
    padding: 5px;
    margin: 10px;
  }

  #updaterIndicator {
    > div {
      display: flex;
      align-items: center;
    }
  }

  #reserves {
    flex-grow: 4;
  }

  #summary {
    display: flex;
    flex-direction: column;
    flex-grow: 2;
    > div {
      display: flex;
      flex-direction: ${(props) => (props.isMobile ? "column" : "row")};
      place-self: center;
    }
  }

  #players {
    display: flex;
    flex-direction: column;
  }

  #players-list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    > div {
      padding: 5px;
    }
  }

  #downloadRepoLink {
    display: inline-flex;
    color: white;
  }
`;

export default EventHeader;
